import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Button,
  HStack,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card.js';
import { getData, postData, deleteData } from 'utils/httpClient';

/*
  Vista DOCENTES del dictado :id (Módulo 4).

  Requerimiento:
    - Permitir asignar MÚLTIPLES docentes a un dictado.

  Endpoints:
    GET    /dictated-courses/:id/teachers                 -> lista asignaciones
    POST   /dictated-courses/:id/teachers  body: { employeeId, role? }
    DELETE /dictated-courses/:id/teachers/:assignmentId   -> baja lógica

  Notas:
    - El combo carga TODOS los empleados activos; al asignar uno por primera
      vez, el backend marca automáticamente is_teacher=1. Esto evita tener
      que exponer un toggle separado cuando la nómina todavía no distingue
      docentes del resto del personal.
*/
export default function DictatedCourseTeachersView() {
  const { id } = useParams();
  const [teachers, setTeachers] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const loadTeachers = async () => {
    try {
      // Trae empleados (cualquiera puede ser candidato a docente).
      // Si el cliente quiere restringir solo a is_teacher=1, cambiar a
      // `/person/employee?page=1&limit=500&isTeacher=1`.
      const res = await getData('/person/employee?page=1&limit=500');
      setTeachers(res?.payload?.people || []);
    } catch (error) {
      toast({
        title: 'Error al cargar empleados',
        description: error.message,
        status: 'error',
      });
    }
  };

  const loadAssigned = async () => {
    try {
      const res = await getData(`/dictated-courses/${id}/teachers`);
      setAssigned(res?.payload?.teachers || []);
    } catch (error) {
      // No romper la UI si el dictado no existe o similar; mostramos
      // el listado vacío y el toast informa el problema.
      toast({
        title: 'Error al cargar docentes asignados',
        description: error.message,
        status: 'error',
      });
    }
  };

  useEffect(() => {
    loadTeachers();
    if (id) loadAssigned();
  }, [id]);

  const assign = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await postData(`/dictated-courses/${id}/teachers`, {
        employeeId: Number(selected),
      });
      toast({ title: 'Docente asignado', status: 'success' });
      setSelected('');
      loadAssigned();
    } catch (error) {
      // Propagar el mensaje real del backend (409 duplicado, 404 dictado
      // no encontrado, etc.) en vez de un genérico.
      toast({
        title: 'No se pudo asignar docente',
        description: error.message,
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const unassign = async (assignId) => {
    try {
      await deleteData(`/dictated-courses/${id}/teachers/${assignId}`);
      toast({ title: 'Docente removido', status: 'success' });
      loadAssigned();
    } catch (error) {
      toast({
        title: 'No se pudo remover docente',
        description: error.message,
        status: 'error',
      });
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={5}>
        <Heading size="md" mb={4}>
          Docentes asignados - Dictado #{id}
        </Heading>

        <HStack mb={4} spacing={3}>
          <Select
            placeholder="Seleccione un docente"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            maxW="400px"
          >
            {teachers.map((t) => (
              <option key={t.id} value={t?.employee?.id}>
                {t.name} {t.lastName} ({t?.employee?.employeeTitle || 'Docente'})
              </option>
            ))}
          </Select>
          <Button
            colorScheme="blue"
            onClick={assign}
            isDisabled={!selected || submitting}
          >
            Asignar
          </Button>
        </HStack>

        {assigned.length === 0 ? (
          <Text>Sin docentes asignados.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Docente</Th>
                <Th>Cargo</Th>
                <Th>Acción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {assigned.map((a) => (
                <Tr key={a.id}>
                  <Td>
                    {a?.employee?.person?.name}{' '}
                    {a?.employee?.person?.lastName}
                  </Td>
                  <Td>{a?.employee?.employeeTitle || '-'}</Td>
                  <Td>
                    <IconButton
                      aria-label="Quitar"
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => unassign(a.id)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
